// India Post pincode → city + state lookup.
// Free public API; CORS-enabled; no auth. See https://api.postalpincode.in/
//
// We translate the returned state name to the 2-digit GST state code used by
// the GST split logic in this app (`accounts.place_of_supply`).

export interface PincodeLookupResult {
  ok: true;
  city: string;
  state: string;
  stateCode: string; // 2-digit GST state code (e.g., "07" for Delhi)
}

export interface PincodeLookupError {
  ok: false;
  reason: "not_found" | "network" | "bad_response";
}

// Official 2-digit GST state codes. Source: GSTIN structure spec.
// Codes that map to multiple historical names (25 → DD merged into 26) point
// to the current canonical entity.
const STATE_NAME_TO_GST_CODE: Record<string, string> = {
  "jammu and kashmir": "01",
  "himachal pradesh": "02",
  "punjab": "03",
  "chandigarh": "04",
  "uttarakhand": "05",
  "uttaranchal": "05",
  "haryana": "06",
  "delhi": "07",
  "rajasthan": "08",
  "uttar pradesh": "09",
  "bihar": "10",
  "sikkim": "11",
  "arunachal pradesh": "12",
  "nagaland": "13",
  "manipur": "14",
  "mizoram": "15",
  "tripura": "16",
  "meghalaya": "17",
  "assam": "18",
  "west bengal": "19",
  "jharkhand": "20",
  "odisha": "21",
  "orissa": "21",
  "chhattisgarh": "22",
  "chattisgarh": "22",
  "madhya pradesh": "23",
  "gujarat": "24",
  "dadra and nagar haveli and daman and diu": "26",
  "daman and diu": "26",
  "dadra and nagar haveli": "26",
  "maharashtra": "27",
  "andhra pradesh": "37",
  "karnataka": "29",
  "goa": "30",
  "lakshadweep": "31",
  "kerala": "32",
  "tamil nadu": "33",
  "puducherry": "34",
  "pondicherry": "34",
  "andaman and nicobar islands": "35",
  "andaman & nicobar islands": "35",
  "telangana": "36",
  "ladakh": "38",
};

function stateNameToCode(name: string): string {
  const key = name.trim().toLowerCase();
  return STATE_NAME_TO_GST_CODE[key] ?? "";
}

export async function lookupPincode(
  pincode: string,
): Promise<PincodeLookupResult | PincodeLookupError> {
  if (!/^\d{6}$/.test(pincode)) {
    return { ok: false, reason: "not_found" };
  }
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
    if (!res.ok) return { ok: false, reason: "network" };
    const data = (await res.json()) as Array<{
      Status?: string;
      PostOffice?: Array<{ District?: string; State?: string; Block?: string }>;
    }>;
    const entry = data?.[0];
    if (!entry || entry.Status !== "Success" || !entry.PostOffice?.length) {
      return { ok: false, reason: "not_found" };
    }
    const po = entry.PostOffice[0]!;
    // India Post returns "District" (administrative) and sometimes "Block" (urban). Prefer Block when it's
    // distinct from District (denser city name); fall back to District.
    const city = (po.Block && po.Block !== po.District ? po.Block : po.District) ?? "";
    const state = po.State ?? "";
    return {
      ok: true,
      city,
      state,
      stateCode: stateNameToCode(state),
    };
  } catch {
    return { ok: false, reason: "network" };
  }
}
